import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { HEX, Theme, Themes, ThemeKey } from 'src/themes-v2';
import { fromLocalStorage, mergeIntoLocalStorage } from 'util/util';
import { useGlobalStore } from './global';
import Menu from '../scenes/menu';


interface ThemePersistedState {
    themeKey: ThemeKey
}

type ThemeValueKey = keyof Theme['values'];
type ThemeSetKey = keyof Theme['sets'];


/**
 * Types representing the `values` and `sets` from a theme but converted entirely to phaser
 * color objects.
 */
type ThemeValuesAsPhaseColors = {
    [T in keyof Theme['values']]: Phaser.Display.Color
}

type ThemeSetsAsPhaseColors = {
    [T in keyof Theme['sets']]: Phaser.Display.Color[]
}

const useThemeStore = defineStore('theme-store', () => {
    let persisted : ThemePersistedState = fromLocalStorage('theme-store', {
        themeKey: 'default'
    })

    const themeKey = ref<ThemeKey>(persisted.themeKey ?? 'default');
    const theme = ref<Theme>(Themes[persisted.themeKey] ?? Themes.default);
    const fallbackColor = Phaser.Display.Color.HexStringToColor('#000000');

    /**
     * Values and sets are converted to phaser color objects upfront whenever the theme changes so that we don't
     * have to keep created color objects on each function call.
     */
    const valuesAsPhaserColors = computed<ThemeValuesAsPhaseColors>(() => {
        return Object.entries(theme.value.values).reduce<ThemeValuesAsPhaseColors>((acc, curr) => {
            acc[curr[0]] = Phaser.Display.Color.HexStringToColor(curr[1]);
            return acc;
        }, {} as ThemeValuesAsPhaseColors);
    });

    const setsAsPhaserColors = computed<ThemeSetsAsPhaseColors>(() => {
        return Object.entries(theme.value.sets).reduce<ThemeSetsAsPhaseColors>((acc, curr) => {
            let key = curr[0], set = curr[1];
            acc[key] = set.map(hex => Phaser.Display.Color.HexStringToColor(hex))
            return acc;
        }, {} as ThemeSetsAsPhaseColors);
    });

    /**
     * Given a key for a color set within the current theme, returns a random color from
     * within the set as a phaser color object.
     * 
     * @param key 
     * @returns 
     */
    const randomFromSet = (key: ThemeSetKey) : Phaser.Display.Color => {
        let set = setsAsPhaserColors.value[key];
        if (set){
            return Phaser.Utils.Array.GetRandom(set)
        }
        return fallbackColor;
    }

    /**
     * A key/value cache where the key is an `accessKey` as passed into `randomUniqueFromSet()` and the value is an
     * array of colors (as numbers) which have already been returned by `randomUniqueFromSet()` for the same access key.
     */
    const uniqueAlreadyPicked = ref<{ [key:string]: number[] }>({}); //Keeps track of already-selected colors for given access key
    const randomUniqueFromSet = (accessKey: string, key: ThemeSetKey) : Phaser.Display.Color => {
        let set = setsAsPhaserColors.value[key];
        if (set){
            /**
             * Init array to record which colors have already been picked or reset it if it already
             * includes all possible colors from the set.
             */
            let alreadyPicked = uniqueAlreadyPicked.value[accessKey];
            if (!alreadyPicked || alreadyPicked.length === set.length) {
                alreadyPicked = uniqueAlreadyPicked.value[accessKey] = [];
            }
            /**
             * Get a random hex and check if it has already been selected. If so, recursively 
             * find another.
             */
            let randColor = <Phaser.Display.Color> Phaser.Utils.Array.GetRandom(set);
            if (alreadyPicked.includes(randColor.color)){
                return randomUniqueFromSet(accessKey, key);
            }

            uniqueAlreadyPicked.value[accessKey].push(randColor.color); //Add just selected color to unique array
            return randColor;
        }
        return fallbackColor;
    }

    /**
     * Clears all cached colors used w/ randomUniqueFromSet(). Important to call this if using the same `accessKey` with
     * a new set of colors as not doing so will eventually cause safety checks to fail and cause an infinite loop.
     * This can be called manually, but is also called automatically whenever a theme change takes place.
     */
    const resetRandomUniqueCache = () => uniqueAlreadyPicked.value = {};

    /**
     * Returns a theme set where each hex value has been converted to a number (for
     * use with phaser object to set `tint`)
     * 
     * @param key - Key for a set included in the current theme
     */
    const getSetAsTint = (key: ThemeSetKey) : number[] => {
        let set = setsAsPhaserColors.value[key];
        if (set){
            return set.map(phaserColor => phaserColor.color)
        }
        return [];
    }

    const value = (key: ThemeValueKey) : HEX => {
        return theme.value.values[key] ?? '#000000';
    }

    /**
     * Converts a theme value from a hex string to a number
     * 
     * @param key - theme value key
     * @returns 
     */
    const tint = (key: ThemeValueKey) : number => {
        return valuesAsPhaserColors.value[key]?.color ?? fallbackColor.color;
    }

    /**
     * Converts a theme value from a hex string to a Phaser color objectc.
     * 
     * @param key - theme value key
     * @returns 
     */
    const color = (key: keyof Theme['values']) : Phaser.Display.Color => {
        return valuesAsPhaserColors.value[key] ?? fallbackColor;
    }

    const getTheme = (key: ThemeKey) => {
        let t = Themes[key];
        if (t){
            return t;
        }
    }

    const setTheme = (key: ThemeKey) => {
        const global = useGlobalStore();
        let t = Themes[key];
        if (t){
            themeKey.value = key;
            theme.value = t;
            resetRandomUniqueCache();
            mergeIntoLocalStorage<ThemePersistedState>('theme-store', { themeKey: key });
            if (global.activeSceneKey === 'Menu'){
                (<Menu> global.sceneState.scene)?.updateBgFx();
            }
        }
    }

    const menuBg = computed(() => theme.value.menuBg);
    const gameBg = computed(() => theme.value.gameBg);
    const values = computed(() => theme.value.values);

    return {
        themeKey,
        theme,
        menuBg,
        gameBg,
        values,
        value,
        randomFromSet,
        randomUniqueFromSet,
        resetRandomUniqueCache,
        getSetAsTint,
        tint,
        color,
        getTheme,
        setTheme
    }
})

export { useThemeStore };